import React, { useState } from 'react';
import { 
  QuestionMarkCircleIcon, 
  PlusIcon, 
  DocumentTextIcon, 
  UserGroupIcon,
  UserPlusIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon
} from '@heroicons/react/24/outline';

const Instructions = () => {
  const [activeTab, setActiveTab] = useState('project');

  const tabs = [
    { id: 'project', name: 'Создание проекта', icon: PlusIcon },
    { id: 'keywords', name: 'Ключевые слова', icon: DocumentTextIcon },
    { id: 'chats', name: 'Добавление чатов', icon: UserGroupIcon },
    { id: 'accounts', name: 'Добавление аккаунтов', icon: UserPlusIcon },
  ];

  const ProjectCreationGuide = () => (
    <div className="space-y-6">
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-start">
          <QuestionMarkCircleIcon className="w-5 h-5 text-blue-600 mr-2 mt-0.5" />
          <div>
            <h3 className="text-blue-800 font-medium">Что такое проект?</h3>
            <p className="text-blue-700 text-sm mt-1">
              Проект - это отдельная lead generation кампания со своими критериями поиска, целевыми группами и каналом доставки результатов.
            </p>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold mb-4">Шаг 1: Переход к созданию проекта</h3>
        <div className="steps space-y-4">
          <div className="flex items-start space-x-3">
            <div className="bg-blue-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-medium">1</div>
            <div>
              <p className="font-medium">Откройте раздел "Projects"</p>
              <p className="text-gray-600 text-sm">В левой навигации нажмите на раздел "Projects"</p>
            </div>
          </div>
          <div className="flex items-start space-x-3">
            <div className="bg-blue-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-medium">2</div>
            <div>
              <p className="font-medium">Нажмите "New Project"</p>
              <p className="text-gray-600 text-sm">В правом верхнем углу найдите синюю кнопку "New Project"</p>
            </div>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold mb-4">Шаг 2: Основные настройки</h3>
        <div className="space-y-4">
          <div className="border rounded-lg p-4">
            <h4 className="font-medium text-green-700 mb-2">✅ Обязательные поля</h4>
            <ul className="space-y-2 text-sm">
              <li><strong>Название проекта:</strong> Понятное имя для вашей кампании (например, "Автомобильные продажи")</li>
              <li><strong>Bot Token:</strong> Токен Telegram бота для отправки результатов</li>
              <li><strong>Report Group ID:</strong> ID группы/чата, куда будут отправляться найденные лиды</li>
            </ul>
          </div>

          <div className="border rounded-lg p-4">
            <h4 className="font-medium text-blue-700 mb-2">📋 Как получить Bot Token</h4>
            <ol className="space-y-1 text-sm list-decimal list-inside">
              <li>Напишите @BotFather в Telegram</li>
              <li>Отправьте команду /newbot</li>
              <li>Следуйте инструкциям для создания бота</li>
              <li>Скопируйте полученный токен (формат: 123456789:ABC-DEF1234ghIkl-zyx57W2v1u123ew11)</li>
            </ol>
          </div>

          <div className="border rounded-lg p-4">
            <h4 className="font-medium text-blue-700 mb-2">🎯 Как получить Report Group ID</h4>
            <ol className="space-y-1 text-sm list-decimal list-inside">
              <li>Добавьте бота(@username_to_id_bot) в группу/чат</li>
              <li>Добавьте Антона(@amalmberg) в группу/чат как администратора</li>
              <li>Бот автоматически отправит в чат сообщение с ID группы</li>
              <li>ID группы имеет формат: -1001234567890 (начинается с минуса)</li>
            </ol>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold mb-4">Шаг 3: Сохранение проекта</h3>
        <div className="flex items-start space-x-3">
          <div className="bg-green-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-medium">3</div>
          <div>
            <p className="font-medium">Нажмите "Save Project"</p>
            <p className="text-gray-600 text-sm">После заполнения всех полей нажмите кнопку сохранения</p>
          </div>
        </div>
      </div>

      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
        <div className="flex items-start">
          <ExclamationTriangleIcon className="w-5 h-5 text-yellow-600 mr-2 mt-0.5" />
          <div>
            <h3 className="text-yellow-800 font-medium">Важно!</h3>
            <p className="text-yellow-700 text-sm mt-1">
              После создания проекта обязательно настройте ключевые слова и добавьте целевые группы, иначе проект не будет работать.
            </p>
          </div>
        </div>
      </div>
    </div>
  );

  const KeywordSystemGuide = () => (
    <div className="space-y-6">
      <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
        <div className="flex items-start">
          <DocumentTextIcon className="w-5 h-5 text-purple-600 mr-2 mt-0.5" />
          <div>
            <h3 className="text-purple-800 font-medium">3-уровневая система ключевых слов</h3>
            <p className="text-purple-700 text-sm mt-1">
              Система позволяет точно настроить поиск релевантных сообщений через три типа ключевых слов.
            </p>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold mb-4">Уровень 1: Primary Keywords (Основные)</h3>
        <div className="space-y-4">
          <div className="border-l-4 border-blue-500 pl-4">
            <h4 className="font-medium text-blue-700">Принцип работы:</h4>
            <p className="text-gray-600 text-sm">Хотя бы ОДНО слово из этого списка должно быть в сообщении</p>
          </div>
          
          <div className="bg-gray-50 p-4 rounded-lg">
            <h4 className="font-medium mb-2">Пример:</h4>
            <div className="bg-white p-3 rounded border">
              <code className="text-sm">кредит, займ, инвестиции, финансы</code>
            </div>
            <p className="text-sm text-gray-600 mt-2">
              Система найдет сообщения со словами: "нужен кредит", "выгодный займ", "инвестиции в недвижимость"
            </p>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold mb-4">Уровень 2: Secondary Keywords (Дополнительные)</h3>
        <div className="space-y-4">
          <div className="border-l-4 border-green-500 pl-4">
            <h4 className="font-medium text-green-700">Принцип работы:</h4>
            <p className="text-gray-600 text-sm">Добавляют префикс "+" и уточняют поиск. Если указаны - хотя бы одно должно совпасть</p>
          </div>
          
          <div className="bg-gray-50 p-4 rounded-lg">
            <h4 className="font-medium mb-2">Пример:</h4>
            <div className="bg-white p-3 rounded border">
              <code className="text-sm">+срочно, +сегодня, +выгодно, +без_справок</code>
            </div>
            <p className="text-sm text-gray-600 mt-2">
              Сообщение "нужен кредит срочно" пройдет фильтр (есть primary "кредит" + secondary "срочно")
            </p>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold mb-4">Уровень 3: Minus Words (Исключения)</h3>
        <div className="space-y-4">
          <div className="border-l-4 border-red-500 pl-4">
            <h4 className="font-medium text-red-700">Принцип работы:</h4>
            <p className="text-gray-600 text-sm">Добавляют префикс "-". Если ЛЮБОЕ слово найдено - сообщение отклоняется</p>
          </div>
          
          <div className="bg-gray-50 p-4 rounded-lg">
            <h4 className="font-medium mb-2">Пример:</h4>
            <div className="bg-white p-3 rounded border">
              <code className="text-sm">-спам, -реклама, -развод, -мошенники</code>
            </div>
            <p className="text-sm text-gray-600 mt-2">
              Сообщение "кредит - не реклама, честно!" будет отклонено из-за слова "реклама"
            </p>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold mb-4">Алгоритм проверки сообщения</h3>
        <div className="space-y-3">
          <div className="flex items-center space-x-3">
            <div className="bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-medium">1</div>
            <div>
              <p className="font-medium">Проверка Minus Words</p>
              <p className="text-gray-600 text-sm">Если найдено любое исключение → ❌ ОТКЛОНИТЬ</p>
            </div>
          </div>
          <div className="flex items-center space-x-3">
            <div className="bg-blue-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-medium">2</div>
            <div>
              <p className="font-medium">Проверка Primary Keywords</p>
              <p className="text-gray-600 text-sm">Если не найдено ни одного основного слова → ❌ ОТКЛОНИТЬ</p>
            </div>
          </div>
          <div className="flex items-center space-x-3">
            <div className="bg-green-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-medium">3</div>
            <div>
              <p className="font-medium">Проверка Secondary Keywords</p>
              <p className="text-gray-600 text-sm">Если указаны, но не найдено ни одного → ❌ ОТКЛОНИТЬ</p>
            </div>
          </div>
          <div className="flex items-center space-x-3">
            <div className="bg-green-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-medium">✓</div>
            <div>
              <p className="font-medium text-green-700">Результат</p>
              <p className="text-gray-600 text-sm">Если все проверки пройдены → ✅ ПРИНЯТЬ</p>
            </div>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold mb-4">Практические примеры</h3>
        <div className="space-y-4">
          <div className="border rounded-lg p-4">
            <h4 className="font-medium text-blue-700 mb-2">📊 Пример 1: Автомобильные продажи</h4>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
              <div>
                <p className="font-medium">Primary:</p>
                <code className="bg-blue-50 px-2 py-1 rounded">продам, куплю, авто, машина</code>
              </div>
              <div>
                <p className="font-medium">Secondary:</p>
                <code className="bg-green-50 px-2 py-1 rounded">+срочно, +недорого, +торг</code>
              </div>
              <div>
                <p className="font-medium">Minus:</p>
                <code className="bg-red-50 px-2 py-1 rounded">-игрушка, -модель, -запчасти</code>
              </div>
            </div>
          </div>

          <div className="border rounded-lg p-4">
            <h4 className="font-medium text-blue-700 mb-2">🏠 Пример 2: Недвижимость</h4>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
              <div>
                <p className="font-medium">Primary:</p>
                <code className="bg-blue-50 px-2 py-1 rounded">квартира, дом, продажа, аренда</code>
              </div>
              <div>
                <p className="font-medium">Secondary:</p>
                <code className="bg-green-50 px-2 py-1 rounded">+центр, +новостройка, +без_посредников</code>
              </div>
              <div>
                <p className="font-medium">Minus:</p>
                <code className="bg-red-50 px-2 py-1 rounded">-аварийная, -под_снос, -обмен</code>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="bg-green-50 border border-green-200 rounded-lg p-4">
        <div className="flex items-start">
          <CheckCircleIcon className="w-5 h-5 text-green-600 mr-2 mt-0.5" />
          <div>
            <h3 className="text-green-800 font-medium">Тестирование ключевых слов</h3>
            <p className="text-green-700 text-sm mt-1">
              В интерфейсе проекта есть кнопка "Test Keywords" для проверки работы вашей конфигурации на примере сообщения.
            </p>
          </div>
        </div>
      </div>
    </div>
  );

  const ChatAssignmentGuide = () => (
    <div className="space-y-6">
      <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4">
        <div className="flex items-start">
          <UserGroupIcon className="w-5 h-5 text-indigo-600 mr-2 mt-0.5" />
          <div>
            <h3 className="text-indigo-800 font-medium">Назначение групп проектам</h3>
            <p className="text-indigo-700 text-sm mt-1">
              Чтобы проект работал, нужно назначить ему группы для мониторинга. Есть два способа: ручной и автоматический.
            </p>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold mb-4">Способ 1: Ручное назначение (Manual Assignment)</h3>
        <div className="space-y-4">
          <div className="steps space-y-3">
            <div className="flex items-start space-x-3">
              <div className="bg-indigo-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-medium">1</div>
              <div>
                <p className="font-medium">Откройте проект</p>
                <p className="text-gray-600 text-sm">В разделе Projects нажмите на нужный проект</p>
              </div>
            </div>
            <div className="flex items-start space-x-3">
              <div className="bg-indigo-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-medium">2</div>
              <div>
                <p className="font-medium">Перейдите в раздел "Groups"</p>
                <p className="text-gray-600 text-sm">Найдите таб "Groups" в интерфейсе проекта</p>
              </div>
            </div>
            <div className="flex items-start space-x-3">
              <div className="bg-indigo-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-medium">3</div>
              <div>
                <p className="font-medium">Нажмите "Add Groups"</p>
                <p className="text-gray-600 text-sm">Появится окно со списком всех доступных групп</p>
              </div>
            </div>
            <div className="flex items-start space-x-3">
              <div className="bg-indigo-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-medium">4</div>
              <div>
                <p className="font-medium">Выберите группы</p>
                <p className="text-gray-600 text-sm">Отметьте нужные группы галочками и нажмите "Add Selected"</p>
              </div>
            </div>
          </div>

          <div className="bg-gray-50 p-4 rounded-lg">
            <h4 className="font-medium mb-2">💡 Фильтрация групп</h4>
            <p className="text-sm text-gray-600">
              Используйте фильтры для поиска подходящих групп:
            </p>
            <ul className="list-disc list-inside text-sm text-gray-600 mt-2 space-y-1">
              <li><strong>Поиск по названию:</strong> введите ключевые слова</li>
              <li><strong>Фильтр по стране:</strong> выберите нужную страну</li>
              <li><strong>Фильтр по языку:</strong> выберите язык общения</li>
              <li><strong>Фильтр по теме:</strong> выберите подходящую категорию</li>
              <li><strong>Фильтр по качеству:</strong> выберите группы с высоким рейтингом</li>
            </ul>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold mb-4">Способ 2: Автоматическое назначение (Auto Assignment)</h3>
        <div className="space-y-4">
          <div className="steps space-y-3">
            <div className="flex items-start space-x-3">
              <div className="bg-green-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-medium">1</div>
              <div>
                <p className="font-medium">Откройте проект</p>
                <p className="text-gray-600 text-sm">В разделе Projects нажмите на нужный проект</p>
              </div>
            </div>
            <div className="flex items-start space-x-3">
              <div className="bg-green-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-medium">2</div>
              <div>
                <p className="font-medium">Найдите "Auto Assignment"</p>
                <p className="text-gray-600 text-sm">В интерфейсе проекта найдите раздел автоматического назначения</p>
              </div>
            </div>
            <div className="flex items-start space-x-3">
              <div className="bg-green-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-medium">3</div>
              <div>
                <p className="font-medium">Настройте фильтры</p>
                <p className="text-gray-600 text-sm">Выберите критерии для автоматического поиска групп</p>
              </div>
            </div>
            <div className="flex items-start space-x-3">
              <div className="bg-green-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-medium">4</div>
              <div>
                <p className="font-medium">Запустите процесс</p>
                <p className="text-gray-600 text-sm">Нажмите "Run Auto Assignment" для автоматического назначения</p>
              </div>
            </div>
          </div>

          <div className="bg-gray-50 p-4 rounded-lg">
            <h4 className="font-medium mb-2">🎯 Критерии автоматического назначения</h4>
            <ul className="list-disc list-inside text-sm text-gray-600 space-y-1">
              <li><strong>По темам:</strong> группы с подходящими категориями</li>
              <li><strong>По странам:</strong> группы из определенных стран</li>
              <li><strong>По языкам:</strong> группы с нужным языком общения</li>
              <li><strong>По качеству:</strong> только группы с высоким рейтингом</li>
              <li><strong>По активности:</strong> группы с достаточным количеством сообщений</li>
            </ul>
          </div>
        </div>
      </div>

      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
        <div className="flex items-start">
          <ExclamationTriangleIcon className="w-5 h-5 text-amber-600 mr-2 mt-0.5" />
          <div>
            <h3 className="text-amber-800 font-medium">Важные рекомендации</h3>
            <ul className="text-amber-700 text-sm mt-1 space-y-1 list-disc list-inside">
              <li>Не назначайте одну группу нескольким проектам - это может привести к дублированию</li>
              <li>Регулярно проверяйте качество найденных лидов и корректируйте ключевые слова</li>
              <li>Используйте фильтры для назначения только релевантных групп</li>
              <li>Следите за активностью групп - неактивные группы не приносят результатов</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );

  const AccountSetupGuide = () => (
    <div className="space-y-6">
      <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
        <div className="flex items-start">
          <UserPlusIcon className="w-5 h-5 text-orange-600 mr-2 mt-0.5" />
          <div>
            <h3 className="text-orange-800 font-medium">Добавление и авторизация Telegram-аккаунтов</h3>
            <p className="text-orange-700 text-sm mt-1">
              Пошаговый процесс добавления новых аккаунтов в систему с полной настройкой и авторизацией.
            </p>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold mb-4">🚀 Шаг 1: Запуск приложения Telegram</h3>
        <div className="space-y-4">
          <div className="steps space-y-3">
            <div className="flex items-start space-x-3">
              <div className="bg-orange-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-medium">1</div>
              <div>
                <p className="font-medium">Разархивируйте .zip архив с аккаунтом</p>
                <p className="text-gray-600 text-sm">Извлеките все файлы из полученного архива</p>
              </div>
            </div>
            <div className="flex items-start space-x-3">
              <div className="bg-orange-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-medium">2</div>
              <div>
                <p className="font-medium">Поместите в папку файл Telegram.exe</p>
                <p className="text-gray-600 text-sm">Скопируйте исполняемый файл Telegram в папку с аккаунтом</p>
              </div>
            </div>
            <div className="flex items-start space-x-3">
              <div className="bg-orange-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-medium">3</div>
              <div>
                <p className="font-medium">Запустите Telegram.exe</p>
                <p className="text-gray-600 text-sm">Двойной клик по файлу для запуска приложения</p>
              </div>
            </div>
          </div>

          <div className="bg-gray-50 p-4 rounded-lg">
            <h4 className="font-medium mb-2">⚠️ Возможные ситуации:</h4>
            <div className="space-y-2 text-sm">
              <div className="flex items-center space-x-2">
                <span className="w-3 h-3 bg-red-500 rounded-full"></span>
                <span><strong>Открылось окно входа (ввод номера):</strong> Аккаунт бракованный. Пометьте его и переходите к следующему.</span>
              </div>
              <div className="flex items-center space-x-2">
                <span className="w-3 h-3 bg-green-500 rounded-full"></span>
                <span><strong>Вы попали в аккаунт:</strong> Отлично! Продолжаем настройку.</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold mb-4">🛡 Шаг 2: Настройки безопасности</h3>
        <div className="space-y-4">
          <div className="steps space-y-3">
            <div className="flex items-start space-x-3">
              <div className="bg-orange-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-medium">1</div>
              <div>
                <p className="font-medium">Перейдите в Settings → Privacy & Security</p>
                <p className="text-gray-600 text-sm">Откройте настройки конфиденциальности и безопасности</p>
              </div>
            </div>
            <div className="flex items-start space-x-3">
              <div className="bg-orange-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-medium">2</div>
              <div>
                <p className="font-medium">Откройте Active sessions</p>
                <p className="text-gray-600 text-sm">Завершите все сессии, кроме текущей (самой верхней)</p>
              </div>
            </div>
            <div className="flex items-start space-x-3">
              <div className="bg-orange-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-medium">3</div>
              <div>
                <p className="font-medium">Перейдите в Two-Step Verification</p>
                <p className="text-gray-600 text-sm">Настройте двухфакторную аутентификацию</p>
              </div>
            </div>
          </div>

          <div className="border rounded-lg p-4">
            <h4 className="font-medium text-orange-700 mb-2">🔐 Настройка Two-Step Verification:</h4>
            <ol className="space-y-1 text-sm list-decimal list-inside">
              <li>Нажмите <strong>Change Password</strong></li>
              <li>Введите текущий пароль из файла <code className="bg-gray-100 px-1 rounded">2fa.txt</code></li>
              <li>Новый пароль: <code className="bg-orange-100 px-2 py-1 rounded font-medium">trinity-agency</code></li>
              <li>Подсказка: <code className="bg-orange-100 px-2 py-1 rounded font-medium">tri</code></li>
              <li>Нажмите <strong>Skip Email</strong></li>
            </ol>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold mb-4">🔑 Шаг 3: Получение API данных</h3>
        <div className="space-y-4">
          <div className="steps space-y-3">
            <div className="flex items-start space-x-3">
              <div className="bg-orange-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-medium">1</div>
              <div>
                <p className="font-medium">Перейдите на my.telegram.org</p>
                <p className="text-gray-600 text-sm">
                  Откройте в браузере: 
                  <a href="https://my.telegram.org" target="_blank" rel="noopener noreferrer" 
                     className="text-orange-600 hover:text-orange-800 ml-1">https://my.telegram.org</a>
                </p>
              </div>
            </div>
            <div className="flex items-start space-x-3">
              <div className="bg-orange-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-medium">2</div>
              <div>
                <p className="font-medium">Введите номер телефона (без плюса)</p>
                <p className="text-gray-600 text-sm">Используйте номер аккаунта без символа "+"</p>
              </div>
            </div>
            <div className="flex items-start space-x-3">
              <div className="bg-orange-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-medium">3</div>
              <div>
                <p className="font-medium">Введите код, полученный в Telegram</p>
                <p className="text-gray-600 text-sm">Код придет в чат с самим собой</p>
              </div>
            </div>
            <div className="flex items-start space-x-3">
              <div className="bg-orange-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-medium">4</div>
              <div>
                <p className="font-medium">Выберите "API development tools"</p>
                <p className="text-gray-600 text-sm">Найдите раздел для разработчиков</p>
              </div>
            </div>
          </div>

          <div className="border rounded-lg p-4">
            <h4 className="font-medium text-orange-700 mb-2">📝 Создание приложения:</h4>
            <div className="space-y-2 text-sm">
              <div><strong>App title:</strong> <code className="bg-orange-100 px-2 py-1 rounded">номер телефона</code></div>
              <div><strong>Short name:</strong> <code className="bg-orange-100 px-2 py-1 rounded">номер телефона</code></div>
            </div>
            <ol className="space-y-1 text-sm list-decimal list-inside mt-3">
              <li>Нажмите <strong>Create application</strong></li>
              <li>Скопируйте <strong>api_id</strong> и <strong>api_hash</strong></li>
              <li>Нажмите <strong>Cancel</strong></li>
              <li>Нажмите <strong>Log Out</strong></li>
            </ol>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold mb-4">🗂 Шаг 4: Добавление аккаунта в базу</h3>
        <div className="space-y-4">
          <div className="steps space-y-3">
            <div className="flex items-start space-x-3">
              <div className="bg-orange-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-medium">1</div>
              <div>
                <p className="font-medium">Перейдите в админку системы</p>
                <p className="text-gray-600 text-sm">
                  Откройте: 
                  <a href="https://admintdlib-c5cd3c2a31ed.herokuapp.com" target="_blank" rel="noopener noreferrer" 
                     className="text-orange-600 hover:text-orange-800 ml-1">https://admintdlib-c5cd3c2a31ed.herokuapp.com</a>
                </p>
              </div>
            </div>
            <div className="flex items-start space-x-3">
              <div className="bg-orange-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-medium">2</div>
              <div>
                <p className="font-medium">Войдите в систему</p>
                <div className="text-gray-600 text-sm space-y-1">
                  <div>Логин: <code className="bg-gray-100 px-1 rounded">admin</code></div>
                  <div>Пароль: <code className="bg-gray-100 px-1 rounded">admin123</code></div>
                </div>
              </div>
            </div>
            <div className="flex items-start space-x-3">
              <div className="bg-orange-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-medium">3</div>
              <div>
                <p className="font-medium">Перейдите в раздел Accounts</p>
                <p className="text-gray-600 text-sm">Найдите раздел "Accounts" в левой навигации</p>
              </div>
            </div>
            <div className="flex items-start space-x-3">
              <div className="bg-orange-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-medium">4</div>
              <div>
                <p className="font-medium">Нажмите "+ Add Account"</p>
                <p className="text-gray-600 text-sm">Кнопка находится в правом верхнем углу</p>
              </div>
            </div>
          </div>

          <div className="border rounded-lg p-4">
            <h4 className="font-medium text-orange-700 mb-2">📋 Заполните форму:</h4>
            <div className="space-y-2 text-sm">
              <div><strong>Name:</strong> номер телефона (без плюса)</div>
              <div><strong>Phone:</strong> номер телефона (без плюса)</div>
              <div><strong>API ID:</strong> скопированный api_id</div>
              <div><strong>API Hash:</strong> скопированный api_hash</div>
              <div><strong>Type:</strong> <code className="bg-orange-100 px-2 py-1 rounded">default</code></div>
            </div>
            <p className="text-sm mt-3">После заполнения нажмите <strong>Create Account</strong></p>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold mb-4">🧩 Шаг 5: Авторизация аккаунта</h3>
        <div className="space-y-4">
          <div className="steps space-y-3">
            <div className="flex items-start space-x-3">
              <div className="bg-orange-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-medium">1</div>
              <div>
                <p className="font-medium">Нажмите кнопку "Authorize Account"</p>
                <p className="text-gray-600 text-sm">Кнопка появится рядом с созданным аккаунтом</p>
              </div>
            </div>
            <div className="flex items-start space-x-3">
              <div className="bg-orange-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-medium">2</div>
              <div>
                <p className="font-medium">Введите код, полученный в Telegram</p>
                <p className="text-gray-600 text-sm">Код придет в приложение Telegram</p>
              </div>
            </div>
            <div className="flex items-start space-x-3">
              <div className="bg-orange-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-medium">3</div>
              <div>
                <p className="font-medium">Введите 2FA пароль</p>
                <p className="text-gray-600 text-sm">
                  Пароль: <code className="bg-orange-100 px-2 py-1 rounded font-medium">trinity-agency</code>
                </p>
              </div>
            </div>
            <div className="flex items-start space-x-3">
              <div className="bg-green-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-medium">✓</div>
              <div>
                <p className="font-medium text-green-700">Успешная авторизация</p>
                <p className="text-gray-600 text-sm">Увидите сообщение: "Account verified successfully with 2FA password"</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="bg-green-50 border border-green-200 rounded-lg p-4">
        <div className="flex items-start">
          <CheckCircleIcon className="w-5 h-5 text-green-600 mr-2 mt-0.5" />
          <div>
            <h3 className="text-green-800 font-medium">🎉 Готово!</h3>
            <p className="text-green-700 text-sm mt-1">
              Аккаунт успешно добавлен и авторизован в системе. Теперь он готов для участия в процессах сканирования и присоединения к группам.
            </p>
          </div>
        </div>
      </div>

      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
        <div className="flex items-start">
          <ExclamationTriangleIcon className="w-5 h-5 text-amber-600 mr-2 mt-0.5" />
          <div>
            <h3 className="text-amber-800 font-medium">Важные замечания</h3>
            <ul className="text-amber-700 text-sm mt-1 space-y-1 list-disc list-inside">
              <li>Всегда используйте пароль <strong>trinity-agency</strong> для всех аккаунтов</li>
              <li>Обязательно завершайте все старые сессии для безопасности</li>
              <li>Сохраняйте API данные в надежном месте</li>
              <li>При проблемах с авторизацией проверьте правильность API ID и Hash</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );

  const renderContent = () => {
    switch (activeTab) {
      case 'project':
        return <ProjectCreationGuide />;
      case 'keywords':
        return <KeywordSystemGuide />;
      case 'chats':
        return <ChatAssignmentGuide />;
      case 'accounts':
        return <AccountSetupGuide />;
      default:
        return <ProjectCreationGuide />;
    }
  };

  return (
    <div className="max-w-6xl mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 mb-2">Инструкции по использованию</h1>
        <p className="text-gray-600">
          Подробные инструкции по работе с системой Telegram Bot Farm
        </p>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="-mb-px flex space-x-8">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`${
                activeTab === tab.id
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              } whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm flex items-center space-x-2`}
            >
              <tab.icon className="w-5 h-5" />
              <span>{tab.name}</span>
            </button>
          ))}
        </nav>
      </div>

      {/* Content */}
      <div className="bg-gray-50 rounded-lg p-6">
        {renderContent()}
      </div>
    </div>
  );
};

export default Instructions;
